import React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { getSecurity, runSecurityPrescreen } from "./api/securityClient";
import { updateDemandStage } from "../../api/demandsClient";

const Schema = z.object({
  demandId: z.string().min(1),
  dataTypes: z.array(z.string()).min(1, "Select at least one data type"),
  residency: z.enum(["US_ONLY", "US_EU", "GLOBAL"]),
  authModel: z.enum(["OIDC", "SAML", "PKI", "BASIC"]).optional(),
  encryptionTransit: z.boolean().refine((v) => v === true, { message: "Required" }),
  encryptionAtRest: z.boolean().refine((v) => v === true, { message: "Required" }),
  loggingPlan: z.string().min(10, "Describe centralized logging & retention"),
  monitoringPlan: z.string().min(10, "Describe monitoring & alerting"),
  incidentPlan: z.string().min(10, "Describe incident response process"),
  integrationEndpoints: z.string().optional(),
  targetEnv: z.string().min(2, "e.g., AWS GovCloud IL4"),
});

const cx = (...c) => c.filter(Boolean).join(" ");

function Section({ title, subtitle, children, right }) {
  return (
    <section className="rounded-2xl border border-gray-200 bg-white shadow-sm">
      <header className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
          {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
        </div>
        {right}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

export default function SecurityIntake({ demandId }) {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm({
    resolver: zodResolver(Schema),
    defaultValues: {
      demandId,
      dataTypes: [],
      residency: "US_ONLY",
      authModel: "OIDC",
      encryptionTransit: true,
      encryptionAtRest: true,
      loggingPlan: "",
      monitoringPlan: "",
      incidentPlan: "",
      integrationEndpoints: "",
      targetEnv: "AWS GovCloud IL4",
    },
    mode: "onChange",
  });

  const [result, setResult] = React.useState(null);
  const [errMsg, setErrMsg] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [routing, setRouting] = React.useState(false);

  // Reload the last submitted intake and result. Previously the form always opened
  // blank and the result panel always said "Run the pre-screen to see results",
  // even for a demand that had already been assessed.
  React.useEffect(() => {
    if (!demandId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const saved = await getSecurity(demandId);
        if (cancelled || !saved) return;
        if (saved.intake && Object.keys(saved.intake).length) {
          Object.entries(saved.intake).forEach(([k, v]) => {
            if (v !== undefined && v !== null) setValue(k, v);
          });
        }
        if (saved.summary) {
          setResult({
            summary: saved.summary,
            controls: saved.controls || [],
            gaps: saved.gaps || [],
          });
        }
      } catch {
        // A demand with no prior pre-screen is the normal case, not an error worth
        // showing; the submit path reports real failures.
      }
    })();
    return () => { cancelled = true; };
  }, [demandId, setValue]);

  const onSubmit = async (values) => {
    setErrMsg("");
    setResult(null);
    try {
      // The server persists the intake, controls, gaps and risk rating as part of
      // this call, so there is no separate "write artifacts" step to follow it.
      setResult(await runSecurityPrescreen(values.demandId, values));
    } catch (e) {
      setErrMsg(e.message || String(e));
    }
  };

  const routeTo = async (stage, status) => {
    setErrMsg("");
    setRouting(true);
    try {
      await updateDemandStage(demandId, stage, status);
      setNotice(`Demand ${demandId} moved to ${stage}.`);
    } catch (e) {
      setErrMsg(e.message || String(e));
    } finally {
      setRouting(false);
    }
  };

  const dataTypes = watch("dataTypes");
  const toggleType = (t) => {
    const set = new Set(dataTypes || []);
    set.has(t) ? set.delete(t) : set.add(t);
    setValue("dataTypes", Array.from(set), { shouldValidate: true });
  };

  return (
    <div className="mx-auto max-w-6xl p-4">
      {/* Header */}
      <div className="sticky top-0 z-10 -mx-4 mb-3 border-b border-gray-200 bg-white/90 backdrop-blur">
        <div className="mx-4 flex items-center justify-between py-3">
          <div>
           {/* <h1 className="text-xl font-semibold tracking-tight text-gray-900">Security Intake</h1> */}
            <p  className="text-xs text-gray-500">Demand {demandId}</p>
          </div>
          <button
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting}
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-60"
          >
            {isSubmitting ? "Running Pre-Screen…" : "Run Pre-Screen"}
          </button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Left: Intake form (2 cols) */}
        <Section
          className="lg:col-span-2"
          title="Security Intake"
          subtitle="Data types, residency, authentication/authorization, and required plans"
        >
          {/* Data types */}
          <div className="mb-4">
            <div className="mb-1 text-xs font-medium text-gray-600">Data Types</div>
            <div className="flex flex-wrap gap-2">
              {["Public", "PII", "PHI", "FOUO", "CUI", "Secrets"].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggleType(t)}
                  className={cx(
                    "rounded-full border px-3 py-1 text-xs",
                    dataTypes?.includes(t) ? "border-gray-900 bg-gray-900 text-white" : "border-gray-300 hover:bg-gray-50"
                  )}
                >
                  {t}
                </button>
              ))}
            </div>
            {errors.dataTypes && <p className="mt-1 text-xs text-rose-600">{errors.dataTypes.message}</p>}
          </div>

          {/* Residency + Auth */}
          <div className="mb-4 grid gap-3 md:grid-cols-2">
            <div>
              <div className="mb-1 text-xs font-medium text-gray-600">Data Residency</div>
              <select
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                {...register("residency")}
              >
                <option value="US_ONLY">US Only</option>
                <option value="US_EU">US + EU</option>
                <option value="GLOBAL">Global</option>
              </select>
            </div>
            <div>
              <div className="mb-1 text-xs font-medium text-gray-600">AuthN/Z Model</div>
              <select
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                {...register("authModel")}
              >
                <option value="OIDC">OIDC</option>
                <option value="SAML">SAML</option>
                <option value="PKI">PKI</option>
                <option value="BASIC">Basic (discouraged)</option>
              </select>
            </div>
          </div>

          {/* Encryption */}
          <div className="mb-4 grid gap-3 md:grid-cols-2">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="h-4 w-4" {...register("encryptionTransit")} />
              Encryption in transit (TLS 1.2+)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="h-4 w-4" {...register("encryptionAtRest")} />
              Encryption at rest (KMS/CMK)
            </label>
          </div>
          <div className="mb-2 text-xs text-rose-600">
            {errors.encryptionTransit?.message || errors.encryptionAtRest?.message}
          </div>

          {/* Plans */}
          <div className="mb-4">
            <div className="mb-1 text-xs font-medium text-gray-600">Logging & Audit Plan</div>
            <textarea
              rows={3}
              className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm"
              placeholder="Centralized logs (e.g., CloudWatch/SIEM), retention, audit scope…"
              {...register("loggingPlan")}
            />
            {errors.loggingPlan && <p className="mt-1 text-xs text-rose-600">{errors.loggingPlan.message}</p>}
          </div>

          <div className="mb-4 grid gap-3 md:grid-cols-2">
            <div>
              <div className="mb-1 text-xs font-medium text-gray-600">Monitoring Plan</div>
              <textarea
                rows={3}
                className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm"
                placeholder="Metrics, alerts, anomaly detection, escalation…"
                {...register("monitoringPlan")}
              />
              {errors.monitoringPlan && <p className="mt-1 text-xs text-rose-600">{errors.monitoringPlan.message}</p>}
            </div>
            <div>
              <div className="mb-1 text-xs font-medium text-gray-600">Incident Response Plan</div>
              <textarea
                rows={3}
                className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm"
                placeholder="Runbook, roles, comms, tabletop, RTO/RPO…"
                {...register("incidentPlan")}
              />
              {errors.incidentPlan && <p className="mt-1 text-xs text-rose-600">{errors.incidentPlan.message}</p>}
            </div>
          </div>

          {/* Integrations + Env */}
          <div className="mb-4">
            <div className="mb-1 text-xs font-medium text-gray-600">Integration Endpoints (optional)</div>
            <textarea
              rows={2}
              className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm"
              placeholder="List endpoints and data flows (name, direction, payload)…"
              {...register("integrationEndpoints")}
            />
          </div>

          <div className="mb-4">
            <div className="mb-1 text-xs font-medium text-gray-600">Target Environment</div>
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              placeholder="e.g., AWS GovCloud IL4 / IL5"
              {...register("targetEnv")}
            />
            {errors.targetEnv && <p className="mt-1 text-xs text-rose-600">{errors.targetEnv.message}</p>}
          </div>

          {errMsg && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">{errMsg}</div>
          )}
          {notice && (
            <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-900">{notice}</div>
          )}
        </Section>

        {/* Right: Results */}
        <Section
          title="Pre-Screen Report"
          subtitle="Required controls, gaps, mitigations, and environment recommendation"
          right={
            result?.summary ? (
              <span
                className={cx(
                  "rounded-full px-2.5 py-0.5 text-xs font-medium",
                  result.summary.prescreen_pass
                    ? "bg-green-100 text-green-800"
                    : "bg-amber-100 text-amber-800"
                )}
              >
                Risk: {result.summary.risk} • {result.summary.prescreen_pass ? "PASS" : "REVIEW"}
              </span>
            ) : null
          }
        >
          {!result ? (
            <div className="text-sm text-gray-500">Run the pre-screen to see results.</div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-lg border">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Control</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Family</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Required</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Status</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.controls?.map((c, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-3 py-2 font-medium">{c.id}</td>
                        <td className="px-3 py-2">{c.family}</td>
                        <td className="px-3 py-2">{c.required ? "Yes" : "No"}</td>
                        <td className="px-3 py-2">
                          <span
                            className={cx(
                              "rounded-full px-2.5 py-0.5 text-xs font-medium",
                              c.status === "ok" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"
                            )}
                          >
                            {c.status}
                          </span>
                        </td>
                        <td className="px-3 py-2">{c.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {result.gaps?.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
                  <div className="mb-2 font-medium text-amber-900">Gaps & Mitigations</div>
                  <ul className="list-inside list-disc space-y-1 text-amber-900">
                    {result.gaps.map((g, i) => (
                      <li key={i}>
                        <span className="font-medium">{g.control}:</span> {g.issue} —{" "}
                        <span className="italic">{g.mitigation}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* These two used to be alert("...(stub).") calls that moved nothing.
                  They now actually advance the demand's stage. */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => routeTo("TAB", "PENDING")}
                  disabled={!result || routing}
                  className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-60"
                >
                  {routing ? "Routing…"
                    : result?.summary?.prescreen_pass ? "Pass → TAB" : "Pass Anyway → TAB"}
                </button>
                <button
                  onClick={() => routeTo("REQUIREMENTS", "PENDING_FIXES")}
                  disabled={routing}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-60"
                >
                  Fail → Return to Requirements
                </button>
                {result?.reportUrl && (
                  <a
                    href={result.reportUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium hover:bg-gray-50"
                  >
                    Open PDF
                  </a>
                )}
              </div>
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}
